import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Checkbox,
  ListItemText,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  fetchCustomSMS,
  createMessage,
  fetchCustomers,
  createSMSesBulk,
} from "./components/smsService";
import { DataGrid } from "@mui/x-data-grid";
import { useTheme } from "@mui/material";
import { tokens } from "../../../theme";

const CustomSMSPage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const [message, setMessage] = useState("");
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [pastMessages, setPastMessages] = useState([]);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessageText, setSuccessMessageText] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedPastMessageId, setSelectedPastMessageId] = useState("");

  // Load past messages and customers when component mounts
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        const [messages, customerList] = await Promise.all([
          fetchCustomSMS(),
          fetchCustomers(),
        ]);

        // Format past messages
        const formattedMessages = messages.map((message) => ({
          id: message.messageId,
          ...message,
        }));

        // Format customer data to ensure companyId and company are included
        const formattedCustomers = customerList.map((customer) => ({
          ...customer,
          companyId: customer.companyId || "defaultCompanyId", // Assign a default if companyId is null
          company: customer.company || "Unknown Company", // Assign a default name if company is null
        }));

        setPastMessages(formattedMessages);
        setCustomers(formattedCustomers);
      } catch (error) {
        console.error("Error loading messages or customers:", error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  const handleSendMessage = async () => {
    if (!message || !selectedCustomers.length) {
      alert("Please fill in all fields");
      return;
    }

    setLoading(true);

    try {
      if (!customers || customers.length === 0) {
        throw new Error("Customers list is not loaded");
      }

      // Create SMS message object
      const smsMessages = selectedCustomers
        .map((customerName) => {
          const customer = customers.find(
            (c) => c.customerName === customerName
          );
          return customer
            ? {
                phoneNumber: customer.phoneNumber,
                body: message,
                createdBy: "currentUserId", // Use the appropriate user ID
                companyId: customer.companyId || "defaultCompanyId", // Provide default if needed
              }
            : null;
        })
        .filter(Boolean); // Filter out any null values

      if (smsMessages.length === 0) {
        throw new Error("No valid customers to send messages to");
      }

      // Use the bulk function for both single and multiple messages
      await createSMSesBulk(smsMessages);
      setSuccessMessageText(`Message(s) sent successfully `);

      setShowSuccessMessage(true);
      resetForm();
      const messages = await fetchCustomSMS();
      setPastMessages(messages);
    } catch (error) {
      console.error("Error sending message:", error);
      setShowSuccessMessage(true);
      setSuccessMessageText("Failed to send messages. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerChange = (event) => {
    const value = event.target.value;

    if (value.includes("all")) {
      setSelectedCustomers(
        isAllSelected ? [] : customers.map((customer) => customer.customerName)
      );
    } else {
      setSelectedCustomers(value);
    }
  };

  const handleSingleCustomerChange = (customerName) => {
    setSelectedCustomers((prevSelected) =>
      prevSelected.includes(customerName)
        ? prevSelected.filter((name) => name !== customerName)
        : [...prevSelected, customerName]
    );
  };

  const handleCloseSnackbar = () => {
    setShowSuccessMessage(false);
  };

  const resetForm = () => {
    setMessage("");
    setSelectedCustomers([]);
    setSelectedPastMessageId("");
  };

  const handleRowSelection = (params) => {
    const selectedMessage = pastMessages.find(
      (msg) => msg.messageId === params.id
    );
    if (selectedMessage) {
      setSelectedPastMessageId(selectedMessage.messageId);
      setMessage(selectedMessage.body);
    }
  };

  const isAllSelected = selectedCustomers.length === customers.length;

  const columns = [
    { field: "messageId", headerName: "ID", width: 100 },
    { field: "body", headerName: "Message", flex: 1 },
    {
      field: "createdDate",
      headerName: "Created Date",
      width: 150,
      valueGetter: (params) =>
        new Date(params.row.createdDate).toLocaleDateString(),
    },
    {
      field: "isActive",
      headerName: "Action",
      width: 150,
      renderCell: (params) => (
        <Button onClick={() => handleRowSelection(params)}>Select</Button>
      ),
    },
  ];

  return (
    <div style={{ padding: "20px" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "20px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          
          <h2>Create and Send Custom SMS</h2>
        </div>
      </div>

      {/* Message Input */}
      <FormControl fullWidth style={{ marginBottom: "20px" }}>
        <TextField
          label="Custom Message"
          variant="outlined"
          multiline
          rows={4}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          required
        />
      </FormControl>

      {/* Customer Selection */}
      <FormControl fullWidth style={{ marginBottom: "20px" }}>
  <InputLabel>Send to Customers</InputLabel>
  <Select
    multiple
    value={selectedCustomers}
    onChange={handleCustomerChange}
    renderValue={(selected) =>
      selected.length ? selected.join(", ") : "Select Customers"
    }
  >
    <MenuItem
      value="all"
      onClick={() =>
        setSelectedCustomers(
          isAllSelected ? [] : customers.map((customer) => customer.customerName)
        )
      }
    >
      <Checkbox checked={isAllSelected} />
      <ListItemText primary="Select All" />
    </MenuItem>
    {customers.map((customer) => (
      <MenuItem key={customer.customerId} value={customer.customerName}>
        <Checkbox
          checked={selectedCustomers.includes(customer.customerName)}
          onClick={(e) => {
            e.stopPropagation(); // Prevents the Select from closing
            handleSingleCustomerChange(customer.customerName);
          }}
        />
        <ListItemText primary={customer.customerName} />
      </MenuItem>
    ))}
  </Select>
</FormControl>


      {/* Send Button */}
      <Button
        variant="contained"
        color="primary"
        onClick={handleSendMessage}
        disabled={loading || !message || !selectedCustomers.length}
      >
        {loading ? "Sending..." : "Send SMS"}
      </Button>

      {/* DataGrid for Past Messages */}
      <Box
        m="40px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
        }}
      >
        <DataGrid
          rows={pastMessages}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10]}
          getRowId={(row) => row.messageId}
        />
      </Box>

      {/* Snackbar for success messages */}
      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity="success">
          {successMessageText}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default CustomSMSPage;
